import { useEffect, useState } from "react";
import { Gear, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const ROLES = [
  ["grant_manager", "Grant Manager"],
  ["contract_specialist", "Contract Specialist"],
  ["legal_reviewer", "Legal Review"],
];

export default function Settings() {
  const [configs, setConfigs] = useState([]);

  const load = async () => setConfigs((await api.get("/workflow-configs")).data);
  useEffect(() => { load(); }, []);

  const toggle = (key, role) => {
    setConfigs((prev) => prev.map((c) => {
      if (c.key !== key) return c;
      const has = c.steps.includes(role);
      const steps = has ? c.steps.filter((s) => s !== role) : [...c.steps, role];
      // keep canonical order
      const ordered = ROLES.map(([r]) => r).filter((r) => steps.includes(r));
      return { ...c, steps: ordered, dirty: true };
    }));
  };

  const setSla = (key, role, val) => {
    setConfigs((prev) => prev.map((c) => c.key !== key ? c
      : { ...c, sla_days: { ...(c.sla_days || {}), [role]: val }, dirty: true }));
  };

  const save = async (cfg) => {
    if (!cfg.steps.length) return toast.error("Select at least one review step");
    try {
      await api.put(`/workflow-configs/${encodeURIComponent(cfg.key)}`, { steps: cfg.steps, sla_days: cfg.sla_days });
      toast.success(`${cfg.key} sequence saved`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Administration" title="Configurable Approvals">
        Define the sequential review sequence per contract type or procurement method. A record follows the sequence
        matching its procurement method first, then its contract type. Example: give <b>Sole Source</b> an extra Legal
        check while a streamlined type skips it.
      </PageHeader>

      <div className="space-y-4">
        {configs.map((cfg) => (
          <div key={cfg.key} data-testid={`config-${cfg.key.replace(/\s+/g, "-")}`} className="bg-card border border-border rounded-md p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Gear size={18} className="text-primary" />
                <span className="font-head font-semibold">{cfg.key}</span>
                {cfg.is_default && !cfg.dirty && <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">default</span>}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
                {cfg.steps.length === 0 ? "No steps" : cfg.steps.map((s, i) => (
                  <span key={s} className="flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded-sm bg-secondary text-foreground">{ROLES.find(([r]) => r === s)?.[1]}</span>
                    {i < cfg.steps.length - 1 && <ArrowRight size={12} />}
                  </span>
                ))}
                <span className="mx-1">→</span>
                <span className="px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Executed</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-5 mt-4">
              {ROLES.map(([role, label]) => (
                <label key={role} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={`config-${cfg.key.replace(/\s+/g, "-")}-${role}`} checked={cfg.steps.includes(role)} onCheckedChange={() => toggle(cfg.key, role)} />
                  {label}
                </label>
              ))}
              <Button size="sm" className="ml-auto" data-testid={`config-save-${cfg.key.replace(/\s+/g, "-")}`} onClick={() => save(cfg)}>
                <CheckCircle size={15} className="mr-1.5" /> Save
              </Button>
            </div>

            <div className="mt-3 pt-3 border-t border-border/60">
              <div className="overline text-muted-foreground mb-2">Review window (SLA days) — escalates to the administrator when breached</div>
              <div className="flex flex-wrap gap-4">
                {ROLES.filter(([r]) => cfg.steps.includes(r)).map(([role, label]) => (
                  <div key={role} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <input
                      data-testid={`sla-${cfg.key.replace(/\s+/g, "-")}-${role}`}
                      type="number" min="1"
                      className="w-16 h-8 rounded-md border border-input bg-background px-2 text-sm"
                      value={(cfg.sla_days || {})[role] ?? ""}
                      onChange={(e) => setSla(cfg.key, role, Number(e.target.value))}
                    />
                    <span className="text-xs text-muted-foreground">days</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
